export interface IMediaApiConstructors {
  MediaStream?: typeof MediaStream;
  MediaSource?: typeof MediaSource;
}

interface IWindowWithMediaApis extends Window, IMediaApiConstructors {
  ManagedMediaSource?: typeof MediaSource;
}

interface IMethodsComparison {
  methodsMatch: boolean;
  differentMethods: string[];
  addedMethods: string[];
  removedMethods: string[];
}

export interface IMediaApiClassTamperingResult extends IMethodsComparison {
  hasCleanConstructor: boolean;
  hasConstructorInWindow: boolean;
  isNative: boolean;
  detectionTimestamp?: number;
}

export interface IMediaApiTamperingResults {
  MediaStream: IMediaApiClassTamperingResult;
  MediaSource: IMediaApiClassTamperingResult;
  hasTampering: boolean;
}

function isNativeFunction(fn: unknown): boolean {
  try {
    if (typeof fn !== 'function') {
      return false;
    }
    const originalToString: () => string = Function.prototype.toString;
    const fnString: string = originalToString.call(fn);
    return /\[native code\]/.test(fnString);
  } catch (error) {
    return false;
  }
}

function getWindowMediaApiConstructors(): IMediaApiConstructors {
  try {
    const w: IWindowWithMediaApis = window;
    const windowMediaSource: typeof MediaSource | undefined = w.ManagedMediaSource || w.MediaSource;
    const windowMediaStream: typeof MediaStream | undefined = w.MediaStream;
    return {
      MediaStream: typeof windowMediaStream === 'function' ? windowMediaStream : undefined,
      MediaSource: typeof windowMediaSource === 'function' ? windowMediaSource : undefined,
    };
  } catch (error) {
    return {};
  }
}

function getCleanMediaApiConstructors(): IMediaApiConstructors {
  try {
    const iframe: HTMLIFrameElement = document.createElement('iframe');
    iframe.style.display = 'none';
    iframe.style.position = 'absolute';
    iframe.style.left = '-9999px';
    document.documentElement.appendChild(iframe);

    const iframeWindow: Window | null = iframe.contentWindow;

    if (!iframeWindow) {
      iframe.remove();
      return {};
    }

    const iwin: IWindowWithMediaApis = iframeWindow as IWindowWithMediaApis;
    const cleanConstructors: IMediaApiConstructors = {};

    if (typeof iwin.MediaStream === 'function') {
      cleanConstructors.MediaStream = iwin.MediaStream;
    }

    const cleanMediaSource: typeof MediaSource | undefined =
      iwin.ManagedMediaSource || iwin.MediaSource;
    if (typeof cleanMediaSource === 'function') {
      cleanConstructors.MediaSource = cleanMediaSource;
    }

    iframe.remove();
    return cleanConstructors;
  } catch (error) {
    return {};
  }
}

const CLASS_METHODS_TO_CHECK: Record<string, string[]> = {
  MediaStream: [
    'addTrack',
    'removeTrack',
    'getTracks',
    'getVideoTracks',
    'getAudioTracks',
    'clone',
  ],
  MediaSource: [
    'addSourceBuffer',
    'removeSourceBuffer',
    'endOfStream',
    'setLiveSeekableRange',
    'clearLiveSeekableRange',
  ],
};

type TMediaApiConstructor = {
  new (...args: unknown[]): unknown;
  prototype: object;
};

function compareConstructorMethods(
  windowConstructor: TMediaApiConstructor,
  cleanConstructor: TMediaApiConstructor,
  methodsToCheck: string[],
): IMethodsComparison {
  const differentMethods: string[] = [];
  const addedMethods: string[] = [];
  const removedMethods: string[] = [];
  const originalToString: () => string = Function.prototype.toString;

  try {
    const windowMethods: string[] = Object.getOwnPropertyNames(windowConstructor.prototype);
    const cleanMethods: string[] = Object.getOwnPropertyNames(cleanConstructor.prototype);

    const windowMethodsSet: Set<string> = new Set(windowMethods);
    const cleanMethodsSet: Set<string> = new Set(cleanMethods);

    for (const method of windowMethods) {
      if (!cleanMethodsSet.has(method)) {
        addedMethods.push(method);
      }
    }

    for (const method of cleanMethods) {
      if (!windowMethodsSet.has(method)) {
        removedMethods.push(method);
      }
    }

    const methodsToCheckFiltered: string[] = methodsToCheck.filter(
      (method) => !removedMethods.includes(method),
    );

    for (const method of methodsToCheckFiltered) {
      const windowMethod: unknown = (windowConstructor.prototype as Record<string, unknown>)[
        method
      ];
      const cleanMethod: unknown = (cleanConstructor.prototype as Record<string, unknown>)[method];

      if (!windowMethod || !cleanMethod) {
        differentMethods.push(method);
        continue;
      }

      const windowMethodString: string = originalToString.call(windowMethod);
      const cleanMethodString: string = originalToString.call(cleanMethod);

      if (windowMethodString !== cleanMethodString) {
        differentMethods.push(method);
      }
    }

    const methodsMatch: boolean =
      differentMethods.length === 0 && addedMethods.length === 0 && removedMethods.length === 0;

    return {
      methodsMatch,
      differentMethods,
      addedMethods,
      removedMethods,
    };
  } catch (error) {
    return {
      methodsMatch: false,
      differentMethods: [],
      addedMethods: [],
      removedMethods: [],
    };
  }
}

function buildClassTamperingResult(
  className: 'MediaStream' | 'MediaSource',
  windowConstructor?: TMediaApiConstructor,
  cleanConstructor?: TMediaApiConstructor,
): IMediaApiClassTamperingResult {
  const hasConstructorInWindow: boolean = Boolean(windowConstructor);
  const hasCleanConstructor: boolean = Boolean(cleanConstructor);
  const isNative: boolean = hasConstructorInWindow ? isNativeFunction(windowConstructor) : false;

  if (hasConstructorInWindow && hasCleanConstructor) {
    const comparison: IMethodsComparison = compareConstructorMethods(
      windowConstructor as TMediaApiConstructor,
      cleanConstructor as TMediaApiConstructor,
      CLASS_METHODS_TO_CHECK[className],
    );
    return {
      ...comparison,
      hasCleanConstructor,
      hasConstructorInWindow,
      isNative,
    };
  }

  return {
    methodsMatch: true,
    differentMethods: [],
    addedMethods: [],
    removedMethods: [],
    hasCleanConstructor,
    hasConstructorInWindow,
    isNative,
  };
}

export function isClassTampered(result: IMediaApiClassTamperingResult): boolean {
  return result.hasConstructorInWindow && (!result.methodsMatch || !result.isNative);
}

export function checkMediaApiTampering(): IMediaApiTamperingResults {
  const { MediaStream: winMS, MediaSource: winMSE } = getWindowMediaApiConstructors();
  const { MediaStream: cleanMS, MediaSource: cleanMSE } = getCleanMediaApiConstructors();

  const mediaStreamResult: IMediaApiClassTamperingResult = buildClassTamperingResult(
    'MediaStream',
    winMS as TMediaApiConstructor,
    cleanMS as TMediaApiConstructor,
  );
  const mediaSourceResult: IMediaApiClassTamperingResult = buildClassTamperingResult(
    'MediaSource',
    winMSE as TMediaApiConstructor,
    cleanMSE as TMediaApiConstructor,
  );

  const hasTampering: boolean =
    isClassTampered(mediaStreamResult) || isClassTampered(mediaSourceResult);

  return {
    MediaStream: mediaStreamResult,
    MediaSource: mediaSourceResult,
    hasTampering,
  };
}
